import { isElectron } from "react-device-detect";

interface CacheEntry { hash: string; originalText: string; translatedText: string; sourceLang: string; targetLang: string; createdAt: number; }

async function sha256(text: string): Promise<string> {
  const data = new TextEncoder().encode(text.replace(/\s+/g, " ").trim());
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, "0")).join("");
}

let _dir = "";
function getDir(): string {
  if (_dir) return _dir;
  if (isElectron) {
    try {
      const { ipcRenderer } = (window as any).require("electron");
      const loc = ipcRenderer.sendSync("storage-location", "ping") || "";
      if (loc) {
        const p = (window as any).require("path");
        const fs = (window as any).require("fs");
        const d = p.join(loc, "translation_cache");
        if (!fs.existsSync(d)) fs.mkdirSync(d, { recursive: true });
        _dir = d;
      }
    } catch { /* noop */ }
  }
  return _dir;
}

function fsOp(): { readFile(p: string): string; writeFile(p: string, data: string): void; exists(p: string): boolean; join(a: string, b: string): string } | null {
  const dir = getDir();
  if (dir) {
    const p = (window as any).require("path");
    const fs = (window as any).require("fs");
    return {
      readFile: (fp: string) => fs.readFileSync(fp, "utf-8"),
      writeFile: (fp: string, data: string) => fs.writeFileSync(fp, data, "utf-8"),
      exists: (fp: string) => fs.existsSync(fp),
      join: (a: string, b: string) => p.join(a, b),
    };
  }
  return null;
}

// localStorage 兜底（浏览器环境或 Electron 文件系统不可用时）
const LS_PREFIX = "koodo_tr_";

function lsGet(key: string): string | null {
  try { return localStorage.getItem(LS_PREFIX + key); } catch { return null; }
}
function lsSet(key: string, value: string): void {
  try { localStorage.setItem(LS_PREFIX + key, value); } catch { /* 容量满 */ }
}

export async function hashText(text: string, src: string, tgt: string): Promise<string> {
  return sha256(`${src}:${tgt}:${text}`);
}

export async function getCache(hash: string): Promise<CacheEntry | null> {
  const fs = fsOp();
  if (fs) {
    try {
      const fp = fs.join(getDir(), `${hash}.json`);
      if (!fs.exists(fp)) return null;
      return JSON.parse(fs.readFile(fp));
    } catch { /* fall through */ }
  }
  // localStorage fallback
  const raw = lsGet(hash);
  return raw ? JSON.parse(raw) : null;
}

export async function setCache(entry: CacheEntry): Promise<void> {
  const fs = fsOp();
  if (fs) {
    try { fs.writeFile(fs.join(getDir(), `${entry.hash}.json`), JSON.stringify(entry)); return; } catch { /* fall through */ }
  }
  lsSet(entry.hash, JSON.stringify(entry));
}

// --- Page-level cache ---

export interface TranslationItem {
  en: string;
  zh: string;
}

export interface PageCacheEntry {
  bookKey: string;
  pageIndex: number;
  texts: string[];
  items: TranslationItem[];
  createdAt: number;
}

function pageCacheLSKey(bookKey: string, pageIndex: number): string {
  // 用简单拼接，避免异步 sha256
  return `page_${bookKey}_${pageIndex}`;
}

export async function getPageCache(bookKey: string, pageIndex: number): Promise<PageCacheEntry | null> {
  const fs = fsOp();
  if (fs) {
    try {
      const key = await sha256(`${bookKey}_page_${pageIndex}`);
      const fp = fs.join(getDir(), `${key}.json`);
      if (!fs.exists(fp)) return null;
      return JSON.parse(fs.readFile(fp));
    } catch { /* fall through */ }
  }
  // localStorage fallback
  const raw = lsGet(pageCacheLSKey(bookKey, pageIndex));
  return raw ? JSON.parse(raw) : null;
}

export async function setPageCache(entry: PageCacheEntry): Promise<void> {
  const fs = fsOp();
  if (fs) {
    try {
      const key = await sha256(`${entry.bookKey}_page_${entry.pageIndex}`);
      fs.writeFile(fs.join(getDir(), `${key}.json`), JSON.stringify(entry));
      return;
    } catch { /* fall through */ }
  }
  // localStorage fallback
  lsSet(pageCacheLSKey(entry.bookKey, entry.pageIndex), JSON.stringify(entry));
}

// 页面加载时从 localStorage 批量恢复内存缓存
export async function loadAllPageCache(bookKey: string): Promise<Record<number, TranslationItem[]>> {
  const result: Record<number, TranslationItem[]> = {};
  const fs = fsOp();
  if (fs) {
    // 文件系统不支持批量扫描，跳过（按需从 getPageCache 加载）
    return result;
  }
  // localStorage：遍历所有 key，匹配当前 bookKey
  try {
    const prefix = LS_PREFIX + `page_${bookKey}_`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        const pageStr = key.slice(prefix.length);
        const pageIdx = parseInt(pageStr);
        if (!isNaN(pageIdx)) {
          try {
            const entry: PageCacheEntry = JSON.parse(localStorage.getItem(key) || "");
            if (entry.items?.length) {
              result[pageIdx] = entry.items;
            }
          } catch { /* skip corrupt */ }
        }
      }
    }
  } catch {}
  return result;
}
